import {
  BillingCodeDTO,
  FaxContactDTO,
  FormBuilderResponseDTO,
  FormCategoryDTO,
  InsuranceDTO,
  InsurancePaymentDTO,
  MultiMediaResponse,
  PopulateSupportUserDTO,
  TemplateListItemDTO,
  ToDoPaginatedCollectionSummaryDTO,
  VisitNoteMipsDTO,
} from 'dtos';
import createEventBus from 'core/EventBus';
import {
  Claim,
  PatientPaymentTransaction,
  PaymentStatus,
  ServiceAllocation,
} from 'pages/Dashboard/pages/Billing/types';
import {
  Entity,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  InsuranceDialogParams,
} from 'views/EMR/components/Insurance/Dialog/InsuranceDialog';
import {
  InsuranceBalanceExplanationDialogParams,
} from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation/InsuranceBalanceExplanationDialog';
import {
  AppointmentConfirmationParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/AppointmentConfirmation';
import {
  CreateAppointmentDialogParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreateAppointmentDialog';
import {
  ToDoDialogParams,
} from 'pages/Dashboard/pages/Todos/CreateToDoForm';
import {
  DialogParams as PaymentDialogParams,
} from 'views/EMR/Billing/PaymentDialog';
import {
  EOBDialogParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog';
import {
  EOBAdjustmentCodeParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import type {
  PCCEnrollParams,
  PCCEnrollResolvedParams,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/EnrollDialog/types';
import type {
  PCCWalkthroughParams,
} from 'views/EMR/components/PCC/Walkthrough/types';
import {
  AdjustmentBucketField,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  ChargePolicyDialogParams,
} from 'pages/Dashboard/pages/Billing/ChargeMaster/ChargePolicyDialog';
import {
  ViewName as PaymentViewName,
} from 'pages/Dashboard/pages/Billing/Payments';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  PatientDialogParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog';
import {
  AppointmentTypeDialogParams,
} from 'views/EMR/Settings/Appointments/Dialog';
import {
  SecondaryClaimDialogParams,
} from 'pages/Dashboard/pages/Billing/ClaimForm/SecondaryClaimDialog';
import {
  InsuranceResolutionDialogParams,
} from 'pages/Dashboard/pages/Billing/Charges/InsuranceResolutionDialog';
import {
  PayerConfigDialogParams,
} from 'views/EMR/Settings/Payers/Dialog';
import {
  VisitNoteParams as VisitConfigParams,
} from 'views/EMR/Settings/VisitNotes';
import {
  CreateDialogParams,
} from 'views/EMR/Settings/Forms';
import {
  MedicalBackgroundParams,
} from 'views/EMR/components/MedicalBackgroundDialog';
import {
  FieldDialogParams,
} from 'views/EMR/Settings/VisitNotes/FieldDialog';
import {
  AddendumDialogProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/VisitsAndNotes/AddendumDialog';
import {
  ServiceType,
} from 'views/EMR/components/ServiceLookup/type';
import {
  RequestMultiMediaDelete,
} from 'pages/Dashboard/hooks/useDeleteMediaHandler';
import {
  WriteOffDialogProps,
} from 'pages/Dashboard/pages/Billing/Charges/WriteOffDialog';
import {
  PCPDialogParams,
} from 'views/EMR/components/PCPDialog';
import {
  VitalsTrendsDialogParams,
} from 'views/EMR/components/VitalsTrendsDialog';
import {
  FaxDialogParams,
} from 'pages/Dashboard/components/SendFaxDialog';
import {
  DialogParams as EncountersAllocationDialogParams,
} from 'views/EMR/Billing/EncountersAllocation';
import {
  StatementDialogParams,
} from 'pages/Dashboard/pages/Ledger/StatementFormDialog';
import {
  FinalNoticeDialogParams,
} from 'pages/Dashboard/pages/Ledger/FinalNoticeFormDialog';
import {
  PurchaseDialogParams,
} from 'views/EMR/Billing/CreatePurchaseDialog';
import {
  HealthRemarkCodeParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/RemarkCodesPopover';
import {
  ClaimPaymentRemarkCodeParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ClaimPaymentRemarkCodesPopover';
import {
  EOBServiceCodeCommentParams,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBServiceCodeCell';
import {
  AdjustPaymentDialogParams,
} from 'views/EMR/Billing/AdjustPayment';
import {
  ProfilePictureDialogProps,
} from 'views/EMR/components/ProfilePictureDialog';
import {
  ServiceCodeLineUpdateParams,
} from 'views/EMR/components/ServiceLookup/List';
import {
  AddFileProps,
  FileUploadDialogProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import {
  WebcamScanDialogProps,
} from 'views/EMR/components/WebcamScanDialog';
import {
  ImageEditorDialogProps,
} from 'views/EMR/components/ImageEditorDialog';
import {
  BulkClaimStatusDialogProps,
} from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/BulkClaimStatusDialog';
import {
  StartFormWorkflowParams,
  ClinicianFormStepDialogParams,
} from 'views/EMR/components/FormWorkflow/types';
import type {
  ReferringProviderContactDialogParams,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/SendToReferringProviderDialog';

type MediaEventChannel = {
  onMediaDeleted: (mediaPayload: RequestMultiMediaDelete) => void;
  onMediaUpdated: () => void;
  onMediaSignatureDialogOpen: (patientMultimedia: MultiMediaResponse) => void;
  onMediaUploadedToCategory: (params: AddFileProps) => void;
  onUploadMediaToCharts: (params: FileUploadDialogProps) => void;
  onStartWebcamScan: (params: WebcamScanDialogProps) => void;
  onOpenImageEditorDialog: (params: ImageEditorDialogProps) => void;
};
export const mediaEventChannel = createEventBus<MediaEventChannel>();

type InsurancesEventChannel = {
  openInsuranceDialog: (params: InsuranceDialogParams) => void;
  onPatientInsuranceUpdated: (patientId: number, insurance: InsuranceDTO) => void;
  onInsuranceCardAssociationUpdated: (insuranceId: number, mediaIds: number[]) => void;
  onInsuranceCorrection: (insurance: InsuranceDTO, patientId: number) => void;
  onEligibilityCheckRefresh: () => void;
  onRefreshPatientInsurance: (patientId: number) => void;
};

export const insurancesEventChannel = createEventBus<InsurancesEventChannel>();

type AppointmentsEventChannel = {
  onAppointmentConfirmation: (params: AppointmentConfirmationParams) => void;
  onEventCreate: (params: CreateAppointmentDialogParams) => void;
  onEventCreated: (created: boolean, id?: number) => void;
  onEventUpdated: (id?: number, forceRerender?: boolean) => void;
  onEventDeleted: (id?: number) => void;
};

export const appointmentsEventChannel = createEventBus<AppointmentsEventChannel>();

export type BillingEventChannel = {
  // charges
  onChargesInfoChange: (entity: Partial<Entity>) => void;
  onClaimLogChange: (claimId: number) => void;
  onClaimUpsert: (encounterId: number) => void;
  // claims
  onClaimsRowsSelectionChange: () => void;
  onBulkClaimStatusDialogOpen: (params: BulkClaimStatusDialogProps) => void;
  onBulkClaimStatusUpdated: () => void;
  // claim tags
  onClaimTagsUpdated: (claimId: number) => void;
  // prices
  onPriceDialogOpen: (billingChargeInfo: ChargePolicyDialogParams) => void;
  onPricesChanged: () => void;
  // eob
  onRelatedAllocation: (props: [HTMLElement | null, ServiceAllocation[]]) => void;
  onEncountersAllocation: (params: EncountersAllocationDialogParams) => void;
  // purchase
  onPurchaseCreate: (params: PurchaseDialogParams) => void;

  onInsuranceResolve: (params: InsuranceResolutionDialogParams) => void;
  onInsuranceBalanceExplanationDialogOpen: (
    params: InsuranceBalanceExplanationDialogParams,
  ) => void;

};
export const billingEventChannel = createEventBus<BillingEventChannel>();

type ClaimEventChannel = {
  onClaimCreating: (encounterId: number) => void;
  onClaimCreated: (encounterId: number) => void;
  onClaimSplit: (claim: Claim) => void;
  onClaimSplitOpen: (claim: Claim) => void;
  onSecondaryClaimCreateOpen: (params: SecondaryClaimDialogParams) => void;
  onClaimActivityOpen: (params: { claimId: number }) => void;
  onShouldUpdateBillingProvider: () => void;
  onShouldUpdateInsuranceType: () => void;
  onClaimVersionConflict: (needsRefresh: boolean) => void;
};
export const claimEventChannel = createEventBus<ClaimEventChannel>();

export type EOBReviewEditAdjustmentsParams = {
  anchorEl: HTMLElement;
  patientId: number;
  field: AdjustmentBucketField;
};

export type PaymentsEventChannel = {
  onPaymentDialogOpen: (params: PaymentDialogParams | null) => void;
  onEOBDialogOpen: (params: EOBDialogParams | null) => void;
  onPaymentsDataChanged: (type: PaymentViewName) => void;
  onPaymentPrint: (
    payment: PatientPaymentTransaction,
    onComplete?: (isCompleted: boolean) => void,
  ) => void;
  onPaymentDelete: (payment: PatientPaymentTransaction, onComplete?: () => void) => void;
  onSquarePaymentUpdate: (paymentStatus: PaymentStatus) => void;
  onPatientBalanceChange: (paymentStatus?: PaymentStatus) => void;
  onChangeEOBAdjustmentCode: (params: EOBAdjustmentCodeParams) => void;
  onChangeEOBRemarkCodes: (params: HealthRemarkCodeParams) => void;
  onChangeEOBClaimPaymentRemarkCodes: (params: ClaimPaymentRemarkCodeParams) => void;
  onChangeEOBServiceCodeComment: (params: EOBServiceCodeCommentParams) => void;
  onEOBAllocationDelete: (claimId: number, id?: number) => void;
  onEOBAllocationExclude: (claimId: number, id?: number, isExcluded?: boolean) => void;
  onEditEOBPatientAdjustments: (params: EOBReviewEditAdjustmentsParams | null) => void;
  onInsurancePaymentDialogOpen: (insurancePayment: InsurancePaymentDTO) => void;
  onAddWriteoff: (params: WriteOffDialogProps | null) => void;
  onStatementDialog: (params: StatementDialogParams) => void;
  onFinalNoticeDialog: (params: FinalNoticeDialogParams) => void;
  // adjust payment
  onAdjustPayment: (params: AdjustPaymentDialogParams) => void;
  onParseEOBScanOpen: () => void;
};

export const paymentsEventChannel = createEventBus<PaymentsEventChannel>();

type TodosEventChannel = {
  onToDosChanged: (toDoId?: number, patientId?: number) => void;
  onToDoSummaryChanged: (summary: ToDoPaginatedCollectionSummaryDTO) => void;
  onCreateToDo: (params: Omit<ToDoDialogParams, 'onClose'> | null) => void;
};

export const toDosEventChannel = createEventBus<TodosEventChannel>();

type PatientAlertsEventChannel = {
  onPatientAlertsChanged: (patientId?: number, encounterId?: number | null) => void;
};

export const patientAlertsEventChannel = createEventBus<PatientAlertsEventChannel>();

type PatientEventChannel = {
  onCreatePatientDialog: (params: PatientDialogParams) => void;
  onShowPCCConsentForm: (params: PCCPatientActionContext) => void;
  onPatientCreated: (patient: PatientCompact) => void;
  onEditMedicalBackground: (params: MedicalBackgroundParams) => void;
  onViewProfilePicture: (params: ProfilePictureDialogProps) => void;
  onMedicalBackgroundChange: (patientId: number, type?: string) => void;
  onEditPCP: (params: PCPDialogParams) => void;
  onOpenVitalsTrends: (params: VitalsTrendsDialogParams) => void;
  onPanelExtra: (expanded?: boolean) => void;
};

export const patientEventChannel = createEventBus<PatientEventChannel>();

type FaxEventChannel = {
  onFaxChange: () => void;
  onUpdateFaxDetails: (id: number) => void;
  onOpenContacts: (contact?: FaxContactDTO) => void;
  onContactChange: () => void;
  onOpenSendFax: (params?: Partial<FaxDialogParams>) => void;
};

export const faxEventChannel = createEventBus<FaxEventChannel>();

type ReferringProviderEventChannel = {
  onOpenContactDialog: (params: ReferringProviderContactDialogParams) => void;
};

export const referringProviderEventChannel = createEventBus<ReferringProviderEventChannel>();

export type VisitNoteParams = {
  patientId: number;
  encounterId: number;
} & Record<string, unknown>;

type VisitNoteEventChannel = {
  onVisitNoteFinalized: () => void;
  onTemplateDelete: (template: TemplateListItemDTO) => void;
  onSaveAsMasterTemplate: () => void;
  onServiceCodesUpdate: (type: ServiceType) => void;
  onMIPSUpdate: (mipsList: VisitNoteMipsDTO[]) => void;
  onAddAddendum: (data: AddendumDialogProps) => void;
  onAddendumPosted: (encounterId?: number) => void;
  onRelatedInfoUpdate: (params: Partial<VisitNoteParams>) => void;
  onApplyPastNote: (visitNoteId: number) => void;
  onApplyTextField: (key: string, value: string) => void;
  onApplyTextFieldWithDoubleLine: (key: string, value: string) => void;
  onApplyBillField: (key: string, codes: BillingCodeDTO[]) => void;
  onScrollToSection: (field: string) => void;
  onVisitNoteConflict: (needsRefresh: boolean) => void;
  onRequestSave: () => void;
};

export const visitNoteEventChannel = createEventBus<VisitNoteEventChannel>();

export type VisitNoteFormPreviewDialogParams = {
  patient?: PatientCompact | null;
  patientMultiMediaIds: number[];
  includeVisitNote: boolean;
  category: FormCategoryDTO;
};

type VisitNoteFormEventChannel = {
  onOpenVisitNoteFormPreview: (params: VisitNoteFormPreviewDialogParams) => void;
  onOpenStartFormWorkflow: (params: StartFormWorkflowParams) => void;
  onOpenClinicianFormStep: (params: ClinicianFormStepDialogParams) => void;
};

export const visitNoteFormEventChannel = createEventBus<VisitNoteFormEventChannel>();

type ReportEventChannel = {
  onReportCreate: () => void;
  onReportChange: () => void;
};

export const reportEventChannel = createEventBus<ReportEventChannel>();

type FormDocumentUpdate = {
  fileName: string;
  url: string;
  isObjectUrl?: boolean;
} | null;

type FormChangeParams = {
  formId: number;
  template?: Partial<FormBuilderResponseDTO>;
  document?: FormDocumentUpdate;
};

type SettingsEventChannel = {
  onChangeTemplates: () => void;
  onCreateTemplate: (params: CreateDialogParams) => void;
  onConsentFormTemplateChange: (params: FormChangeParams) => void;
  onCreateAppointmentType: (params: AppointmentTypeDialogParams) => void;
  onAppointmentTypesChange: () => void;
  onPayerConfigDialog: (params: PayerConfigDialogParams) => void;
  onPayerConfigChange: () => void;
  onRoomSettingsChange: () => void;
  onTemplateDetails: (type: VisitConfigParams['config']) => void;
  onVisitNoteSettingsChange: (
    type?: VisitConfigParams['config'],
    template?: Pick<TemplateListItemDTO, 'templateName' | 'sectionName' | 'userId'>,
  ) => void;
  onTemplateFieldDialog: (params?: FieldDialogParams) => void;
  onUsersChange: () => void;
  onTagsChange: () => void;
  onServiceFacilityChange: () => void;
  onRenderingProviderChange: () => void;
  onBillingProviderChange: () => void;
  onBillingProviderRuleChange: () => void;
};

export const settingsEventChannel = createEventBus<SettingsEventChannel>();

type AIAssistantEventChannel = {
  onNewUserMessage: (message: string) => void;
  onShowAIAssistantDialog: () => void;
};

export const aiAssistantEventChannel = createEventBus<AIAssistantEventChannel>();

type ServiceLookupEventChannel = {
  onServiceCodeLineUpdate: (params: ServiceCodeLineUpdateParams) => void;
};

export const serviceLookupEventChannel = createEventBus<ServiceLookupEventChannel>();

type PopulateSupportEventChannel = {
  onImpersonateUser: (user: PopulateSupportUserDTO) => void;
};

export const populateSupportEventChannel = createEventBus<PopulateSupportEventChannel>();

export type {
  PCCWalkthroughParams,
};

type PCCEventChannel = {
  onShowPCCEnroll: (payload: PCCEnrollParams) => void;
  onPCCEnrollResolved: (payload: PCCEnrollResolvedParams) => void;
  onShowPCCWalkthrough: (payload?: PCCWalkthroughParams) => void;
};

export const pccEventChannel = createEventBus<PCCEventChannel>();

export type RTMPatientTrendsParams = {
  patient: PatientCompact;
};

type RTMEventChannel = {
  onShowRTMPatientTrends: (payload: RTMPatientTrendsParams) => void;
  onShowRTMThresholdSettings: () => void;
  onRTMPatientDataChange: (patientId: number) => void;
};

export const rtmEventChannel = createEventBus<RTMEventChannel>();
